<script lang="ts">
  import {
    Button,
    Accordion,
    AccordionItem
  } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import {
    faPencil,
    faPlus,
    faRefresh,
    faTrash,
    faRocket
  } from "@fortawesome/free-solid-svg-icons";
  import { editLookup } from "../../../services/stores/lookup-drawer-store.js";
  import {
    createDraftLookup,
    getAllLookups,
    deleteLookup,
    activateLookup
  } from "../../../services/api/data-handler.js";
  import { lookupDrawerStore } from "../../../services/stores/lookup-drawer-store.js";
  import { logInfo, logError, logWarning } from "../../../services/logging/log-handler.js";

  interface LookupItem {
    id: string;
    name: string;
  }

  interface LookupMetadata {
    list: LookupItem[];
    type: "single-select" | "multi-select";
    level: "project" | "customer";
    can_override: boolean;
    system_level: boolean;
  }

  interface GlobalLookup {
    global_lookup_id: string;
    version: number;
    state: string;
    metadata: Record<string, LookupMetadata>;
    effective_from?: string;
    effective_to?: string;
  }

  let currentLookup: GlobalLookup[] = [];
  let isRefreshing = false;

  async function refreshData() {
    isRefreshing = true;
    try {
      const response = await getAllLookups();
      if (response.success && response.data) {
        currentLookup = response.data;
      }
    } catch (error) {
      logError(error, "LookupGrid", undefined, { message: "Error refreshing lookups" });
    } finally {
      isRefreshing = false;
    }
  }

  async function handleCreateDraft() {
    try {
      await createDraftLookup();
      await refreshData();
    } catch (error) {
      logError(error, "LookupGrid", undefined, { message: "Error creating draft" });
    }
  }

  async function handleDelete(lookup: GlobalLookup) {
    try {
      await deleteLookup(lookup.global_lookup_id);
      await refreshData();
    } catch (error) {
      logError(error, "LookupGrid", undefined, { message: "Error deleting lookup" });
    }
  }

  async function handleActivate(lookup: GlobalLookup) {
    try {
      await activateLookup(lookup.global_lookup_id);
      await refreshData();
    } catch (error) {
      logError(error, "LookupGrid", undefined, { message: "Error activating lookup" }); 
    }
  }

  refreshData();

  // Add reactive statement to refresh data when drawer is closed
  $: if (!$lookupDrawerStore.open) {
    refreshData();
  }

  function handleEdit(
    section: string,
    data: LookupMetadata,
    lookup: GlobalLookup
  ) {
    editLookup(
      section as any,
      data.list,
      lookup.global_lookup_id,
      lookup.state
    );
  }

  function getVersionDisplay(lookup: GlobalLookup): string {
    var display = "";
    if (lookup.state === "A") {
      display = `V${lookup.version} • ACTIVE`;
    }
    if (lookup.state === "D") {
      display = `V${lookup.version} • DRAFT`;
    }
    if (lookup.state === "I") {
      display = `V${lookup.version} • INACTIVE`;
    }
    return display;
  }

  function getDateDisplay(lookup: GlobalLookup): string {
    var display = "";
    var from = lookup.effective_from ? formatDateForDisplay(lookup.effective_from) : "1-JAN-2025";
    var to = lookup.effective_to ? formatDateForDisplay(lookup.effective_to) : "TODAY";
    if (lookup.state === "A" || lookup.state === "I") {
      display = `${from} • ${to}`;
    }
    return display;
  }

  function formatDateForDisplay(dateStr: string): string {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function formatSectionName(section: string): string {
    return section
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  function getItemStatus(
    item: LookupItem,
    currentVersion: number,
    section: string
  ): "deleted" | "added" | null {
    if (currentVersion === 1) return null;

    const currentLookupData = currentLookup.find(
      (l) => l.version === currentVersion
    );
    const previousLookupData = currentLookup.find(
      (l) => l.version === currentVersion - 1
    );

    if (!currentLookupData || !previousLookupData) return null;

    const currentItems = currentLookupData.metadata[section]?.list || [];
    const previousItems = previousLookupData.metadata[section]?.list || [];

    const itemExistsInCurrent = currentItems.some((i) => i.id === item.id);
    const itemExistsInPrevious = previousItems.some((i) => i.id === item.id);

    // Only mark as added if the item exists in current version but not in previous
    if (itemExistsInCurrent && !itemExistsInPrevious) return "added";
    if (!itemExistsInCurrent && itemExistsInPrevious) return "deleted";
    return null;
  }

  function getAllItemsForSection(
    section: string,
    currentVersion: number
  ): LookupItem[] {
    const lookupData = currentLookup.find((l) => l.version === currentVersion);
    const previousLookupData = currentLookup.find(
      (l) => l.version === currentVersion - 1
    );

    const currentItems = lookupData?.metadata[section]?.list || [];
    const previousItems = previousLookupData?.metadata[section]?.list || [];

    const allItems = [...currentItems];
    previousItems.forEach((item) => {
      if (!allItems.some((i) => i.id === item.id)) {
        allItems.push(item);
      }
    });

    return allItems;
  }

  function hasChangesInSection(
    section: string,
    currentVersion: number
  ): { hasAdditions: boolean; hasDeletions: boolean } {
    if (currentVersion === 1)
      return { hasAdditions: false, hasDeletions: false };

    const currentLookupData = currentLookup.find(
      (l) => l.version === currentVersion
    );
    const previousLookupData = currentLookup.find(
      (l) => l.version === currentVersion - 1
    );

    if (!currentLookupData || !previousLookupData)
      return { hasAdditions: false, hasDeletions: false };

    const currentItems = currentLookupData.metadata[section]?.list || [];
    const previousItems = previousLookupData.metadata[section]?.list || [];

    const hasAdditions = currentItems.some(
      (item) => !previousItems.some((prevItem) => prevItem.id === item.id)
    );

    const hasDeletions = previousItems.some(
      (item) => !currentItems.some((currentItem) => currentItem.id === item.id)
    );

    return { hasAdditions, hasDeletions };
  }
</script>

<div class="app-grid-header flex items-center justify-left">
  <h3 class="text-base font-bold mr-1">Lookups</h3>
  <Button
    pill={true}
    outline={true}
    class="grid-header-button icon-only"
    size="sm"
    data-testid="grid-header-add-button"
    on:click={handleCreateDraft}
  >
    <FontAwesomeIcon icon={faPlus} />
  </Button>
  <Button
    pill={true}
    outline={true}
    class="grid-header-button icon-only"
    size="sm"
    data-testid="grid-header-refresh-button"
    on:click={refreshData}
  >
    <FontAwesomeIcon icon={faRefresh} />
  </Button>
</div>

{#if currentLookup.length > 0}
  <div class="lookups-wrapper">
    <div class="lookup-container">
    {#each currentLookup as lookup, lookupIndex}

        <div class="single-lookup">
          <div class="lookup-header">
            <div class="lookup-title">
              <h3>{getVersionDisplay(lookup)}</h3>
              {#if lookup.state === "D"}
                <Button
                  class="lookup-button text-icon"
                  size="xs"
                  on:click={() => handleActivate(lookup)}
                  data-testid="activate-lookup-button"
                >
                  <FontAwesomeIcon icon={faRocket} />
                  Activate
                </Button>
                <Button
                  class="lookup-button icon-only"
                  size="xs"
                  on:click={() => handleDelete(lookup)}
                  data-testid="delete-lookup-button"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              {/if}
            </div>
            <div class="lookup-date">{getDateDisplay(lookup)}</div>
          </div>

          <div class="lookup-content">
            <Accordion>
              {#each Object.entries(lookup.metadata).filter(([_, metadata]) => !metadata.system_level) as [section, metadata]}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <AccordionItem>
                  <svelte:fragment slot="header">
                    <div class="accordion-header">
                      <span>{formatSectionName(section)}</span>
                      {#if lookup.version > 1}
                        {#if hasChangesInSection(section, lookup.version).hasAdditions || hasChangesInSection(section, lookup.version).hasDeletions}
                          <span class="update-indicator" data-testid="update-indicator">(UPDATED)</span>
                        {/if}
                      {/if}
                      <div on:click|stopPropagation role="button" tabindex="0">
                        {#if lookup.state !== 'I'}
                          <Button
                            size="xs"
                            class="lookup-button icon-only"
                            data-testid="edit-lookup-button"
                            on:click={() => handleEdit(section, metadata, lookup)}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </Button>
                        {/if}
                      </div>
                    </div>
                  </svelte:fragment>
                  <div class="accordion-content">
                    <div class="item-content">
                      <span class="item-label">Type: </span><span
                        class="select-type"
                        >{metadata.type === "single-select"
                          ? "Single select"
                          : "Multi-select"}</span
                      >
                    </div>
                    <div class="item-content">
                      <span class="item-label">State: </span>
                      <span class="state-value" data-testid="state-value">
                        {metadata.can_override ? "Optional" : "Required"}
                      </span>
                    </div>
                    <div class="item-content">
                      <span class="item-label">Level: </span><span
                        class="helper-value">{metadata.level}</span
                      >
                    </div>
                    <div class="list-label">Items:</div>
                    <ul class="lookup-list">
                      {#each getAllItemsForSection(section, lookup.version) as item}
                        <li
                          class="lookup-item flex items-center justify-between"
                        >
                          <span
                            class:deleted={getItemStatus(
                              item,
                              lookup.version,
                              section
                            ) === "deleted"}
                            class:added={getItemStatus(
                              item,
                              lookup.version,
                              section
                            ) === "added"}
                          >
                            {item.name}
                          </span>
                        </li>
                      {/each}
                    </ul>
                  </div>
                </AccordionItem>
              {/each}
            </Accordion>
          </div>
        </div>
      {/each}
    </div>
  </div>
{:else}
  <div class="p-4">
    <p>No lookups available</p>
  </div>
{/if}
